import PropTypes from "prop-types";

import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

/**
 * Shared right-side panel shell — a drawer that slides in from the right for
 * create/edit flows. Mirrors ModalShell's API (title, subtitle, optional
 * footer actions) but anchored as a full-height side panel.
 *
 * Bodies that already contain their own submit button can omit `actions`.
 */
function SidePanel({ open, onClose, title, subtitle, width, actions, children }) {
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: width },
          maxWidth: "100%",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <MDBox
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        px={3}
        pt={3}
        pb={2}
        flexShrink={0}
      >
        <MDBox>
          <MDTypography variant="h5" fontWeight="bold">
            {title}
          </MDTypography>
          {subtitle && (
            <MDTypography variant="button" color="text" fontWeight="regular">
              {subtitle}
            </MDTypography>
          )}
        </MDBox>
        <IconButton onClick={onClose} size="small" sx={{ mt: -0.5, mr: -1 }} aria-label="Close">
          <Icon>close</Icon>
        </IconButton>
      </MDBox>

      <MDBox flex={1} sx={{ overflowY: "auto", overflowX: "hidden" }} px={3} pb={3}>
        {children}
      </MDBox>

      {actions && (
        <MDBox display="flex" justifyContent="flex-end" gap={1} px={3} py={2} flexShrink={0}>
          {actions}
        </MDBox>
      )}
    </Drawer>
  );
}

SidePanel.defaultProps = {
  subtitle: null,
  width: 480,
  actions: null,
};

SidePanel.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.node,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  actions: PropTypes.node,
  children: PropTypes.node.isRequired,
};

export default SidePanel;
